import { oauthAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { domainToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const kakaoTalk = defineConnector({
  identifier: "kakaoTalk",
  name: "KakaoTalk",
  tagLine: "Kakao login and messaging",
  keywords: "kakao kakaotalk korea messaging login talk memo notifications",
  logo,
  protocol: "http",
  tags: ["communication"],
  defaultUrlPattern: () => domainToRegex("kapi.kakao.com"),
  auth: oauthAuth,
  steps: [
    {
      title: "Create a Kakao application",
      items: [
        {
          kind: "markdown",
          content:
            "Create an app at [developers.kakao.com](https://developers.kakao.com/console/app). Under **Kakao Login › Enable** set the state to **ON**, then under **Kakao Login › Consent items** enable `profile_nickname` (under **Personal information**) and **Send Kakao Talk message** (under **Access permission**).\n\nThose two, plus `profile_image` and sending a message to the signed-in user’s own chat, are everything a new app can do without review. Reading the account email, listing friends, or messaging anyone else needs a business app **and** Korean identity verification of the owner’s Kakao account, which in practice needs a Korean mobile number. A D-U-N-S number covers the business half for overseas companies, but not the identity half.",
        },
      ],
    },
    {
      title: "Configure the redirect URI",
      items: [
        {
          kind: "markdown",
          content:
            "Add this redirect URI under **App › Platform key › REST API key › Redirect URI**:",
        },
        {
          kind: "copyable",
          label: "Redirect URI",
          value: "{API_URL}/connected-apps/oauth/kakaoTalk/callback",
        },
      ],
    },
    {
      title: "Enter your app credentials",
      items: [
        {
          kind: "markdown",
          content:
            "Both live under **App › Platform key › REST API key**: the client ID is the key itself, and the client secret is the **Client secret** code beside it. New keys come with the client secret feature switched on, so the token request has to carry it — generate a code if the field is empty, and check that **Activation** reads **ON**.",
        },
        {
          kind: "input",
          label: "REST API key",
          required: true,
          target: { kind: "oauthConnection", field: "clientId" },
        },
        {
          kind: "input",
          label: "Client secret",
          required: true,
          target: { kind: "oauthConnection", field: "clientSecret" },
        },
      ],
    },
    {
      title: "Authorize your Kakao account",
      items: [
        {
          kind: "markdown",
          content:
            "Kakao refresh tokens last two months and can only be renewed in the final month, so a connection left unused for two months has to be authorized again.",
        },
        {
          kind: "oauth",
          dependsOn: [
            { kind: "oauthConnection", field: "clientId" },
            { kind: "oauthConnection", field: "clientSecret" },
          ],
        },
      ],
    },
  ],
  oauth: {
    grantType: () => "authorization_code",
    authorizationEndpoint: () => "https://kauth.kakao.com/oauth/authorize",
    tokenEndpoint: () => "https://kauth.kakao.com/oauth/token",
    tokenEndpointAuthMethod: () => "client_secret_post",
    clientCredentialsFrom: "db",
    scopes: () => ["profile_nickname", "talk_message"],
  },
  connectionTest: {
    protocol: "http",
    request: () => "https://kapi.kakao.com/v2/user/me",
  },
});
